export const SCALE = 1_000_000;

export type LmsrSide = 'yes' | 'no';

export interface LmsrQuote {
  estimated_shares: number;
  raw_cost: number;
  fee: number;
  total_charge: number;
  refund: number;
  avg_price_per_share: number;
  min_shares_for_slippage: number;
  current_odds: {
    yes: number;
    no: number;
  };
  new_odds_after_purchase: {
    yes: number;
    no: number;
  };
  exceeds_liquidity_cap: boolean;
  max_spend_amount?: number;
}

export interface LmsrSellEstimate {
  payout: number;
  new_odds_after_sale: {
    yes: number;
    no: number;
  };
}

const PROB_CAP = 990_000;
const DEFAULT_FEE_BPS = 100;

const toFixed = (value: number): number => Math.round(value * SCALE);
const fromFixed = (value: number): number => value / SCALE;
const safeRatio = (numerator: number, denominator: number): number => (
  denominator === 0 ? 0 : Math.trunc(numerator / denominator)
);

const expFixed = (xScaled: number): number => {
  const value = toFixed(Math.exp(fromFixed(xScaled)));
  return value > 0 ? value : 0;
};

const lnFixed = (xScaled: number): number => {
  if (xScaled <= 0) return 0;
  return toFixed(Math.log(fromFixed(xScaled)));
};

const lmsrCost = (qYes: number, qNo: number, b: number): number => {
  if (b <= 0) return 0;

  const ratioYes = safeRatio(qYes * SCALE, b);
  const ratioNo = safeRatio(qNo * SCALE, b);
  const maxRatio = Math.max(ratioYes, ratioNo);
  const sumExp = expFixed(ratioYes - maxRatio) + expFixed(ratioNo - maxRatio);
  const lnSum = lnFixed(sumExp) + maxRatio;

  return safeRatio(b * lnSum, SCALE);
};

const lmsrBuyCost = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  deltaQ: number
): number => {
  const newQYes = outcomeIsYes ? qYes + deltaQ : qYes;
  const newQNo = outcomeIsYes ? qNo : qNo + deltaQ;
  return lmsrCost(newQYes, newQNo, b) - lmsrCost(qYes, qNo, b);
};

const lmsrSellPayout = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  deltaQ: number
): number => {
  const newQYes = outcomeIsYes ? Math.max(0, qYes - deltaQ) : qYes;
  const newQNo = outcomeIsYes ? qNo : Math.max(0, qNo - deltaQ);
  const payout = lmsrCost(qYes, qNo, b) - lmsrCost(newQYes, newQNo, b);
  return payout > 0 ? payout : 0;
};

const lmsrProbabilityYes = (qYes: number, qNo: number, b: number): number => {
  if (b <= 0) return SCALE / 2;

  const ratioYes = safeRatio(qYes * SCALE, b);
  const ratioNo = safeRatio(qNo * SCALE, b);
  const maxRatio = Math.max(ratioYes, ratioNo);
  const expYes = expFixed(ratioYes - maxRatio);
  const expNo = expFixed(ratioNo - maxRatio);
  const sumExp = expYes + expNo;

  if (sumExp === 0) return SCALE / 2;
  return safeRatio(expYes * SCALE, sumExp);
};

const lmsrComputeSharesFromBudget = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  budget: number,
  feeBps: number
): number => {
  if (budget <= 0) return 0;

  let maxDelta = SCALE;
  while (maxDelta < budget * 10) {
    const cost = lmsrBuyCost(qYes, qNo, b, outcomeIsYes, maxDelta);
    const fee = safeRatio(cost * feeBps, 10_000);
    if (cost + fee > budget) break;
    maxDelta *= 2;
  }

  let lo = 0;
  let hi = maxDelta;
  for (let i = 0; i < 32; i++) {
    const mid = Math.trunc((lo + hi) / 2);
    const cost = lmsrBuyCost(qYes, qNo, b, outcomeIsYes, mid);
    const fee = safeRatio(cost * feeBps, 10_000);

    if (cost + fee <= budget) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return lo;
};

const exceedsProbabilityCap = (probYes: number, probNo: number): boolean => (
  Math.max(probYes, probNo) > PROB_CAP
);

const computeMaxSpendForProbCap = (
  qYes: number,
  qNo: number,
  b: number,
  outcomeIsYes: boolean,
  feeBps: number
): number => {
  let hi = b * 20;
  let lo = 0;

  const sharesHi = lmsrComputeSharesFromBudget(qYes, qNo, b, outcomeIsYes, hi, feeBps);
  const testQYes = outcomeIsYes ? qYes + sharesHi : qYes;
  const testQNo = outcomeIsYes ? qNo : qNo + sharesHi;
  const testProbYes = lmsrProbabilityYes(testQYes, testQNo, b);
  const testProbNo = SCALE - testProbYes;
  if (!exceedsProbabilityCap(testProbYes, testProbNo)) {
    return hi;
  }

  for (let i = 0; i < 40; i++) {
    const mid = Math.trunc((lo + hi) / 2);
    if (mid <= 0) break;

    const sharesMid = lmsrComputeSharesFromBudget(qYes, qNo, b, outcomeIsYes, mid, feeBps);
    const nextQYes = outcomeIsYes ? qYes + sharesMid : qYes;
    const nextQNo = outcomeIsYes ? qNo : qNo + sharesMid;
    const probYes = lmsrProbabilityYes(nextQYes, nextQNo, b);
    const probNo = SCALE - probYes;

    if (exceedsProbabilityCap(probYes, probNo)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }

  return lo;
};

export const estimateLmsrBuyQuote = (
  qYes: number,
  qNo: number,
  b: number,
  side: LmsrSide,
  spendAmount: number,
  feeBps: number = DEFAULT_FEE_BPS
): LmsrQuote | null => {
  if (spendAmount <= 0 || b <= 0) return null;

  const outcomeIsYes = side === 'yes';
  const budget = Math.trunc(spendAmount * SCALE);
  const deltaQ = lmsrComputeSharesFromBudget(qYes, qNo, b, outcomeIsYes, budget, feeBps);
  const rawCost = lmsrBuyCost(qYes, qNo, b, outcomeIsYes, deltaQ);
  const fee = safeRatio(rawCost * feeBps, 10_000);
  const totalCharge = rawCost + fee;
  const refund = budget - totalCharge;
  const newQYes = outcomeIsYes ? qYes + deltaQ : qYes;
  const newQNo = outcomeIsYes ? qNo : qNo + deltaQ;
  const currentProbYes = lmsrProbabilityYes(qYes, qNo, b);
  const newProbYes = lmsrProbabilityYes(newQYes, newQNo, b);
  const exceedsLiquidityCap = exceedsProbabilityCap(newProbYes, SCALE - newProbYes);
  const quote: LmsrQuote = {
    estimated_shares: fromFixed(deltaQ),
    raw_cost: fromFixed(rawCost),
    fee: fromFixed(fee),
    total_charge: fromFixed(totalCharge),
    refund: Math.max(0, fromFixed(refund)),
    avg_price_per_share: deltaQ > 0 ? totalCharge / deltaQ : 0,
    min_shares_for_slippage: Math.trunc(deltaQ * 0.98 / SCALE),
    current_odds: {
      yes: (currentProbYes / SCALE) * 100,
      no: ((SCALE - currentProbYes) / SCALE) * 100,
    },
    new_odds_after_purchase: {
      yes: (newProbYes / SCALE) * 100,
      no: ((SCALE - newProbYes) / SCALE) * 100,
    },
    exceeds_liquidity_cap: exceedsLiquidityCap,
  };

  if (exceedsLiquidityCap) {
    quote.max_spend_amount = fromFixed(computeMaxSpendForProbCap(qYes, qNo, b, outcomeIsYes, feeBps));
  }

  return quote;
};

export const estimateLmsrSellPayout = (
  qYes: number,
  qNo: number,
  b: number,
  side: LmsrSide,
  sharesAmount: number
): LmsrSellEstimate | null => {
  if (sharesAmount <= 0 || b <= 0) return null;

  const outcomeIsYes = side === 'yes';
  const deltaQ = Math.trunc(sharesAmount * SCALE);
  const payout = lmsrSellPayout(qYes, qNo, b, outcomeIsYes, deltaQ);
  const newQYes = outcomeIsYes ? Math.max(0, qYes - deltaQ) : qYes;
  const newQNo = outcomeIsYes ? qNo : Math.max(0, qNo - deltaQ);
  const newProbYes = lmsrProbabilityYes(newQYes, newQNo, b);

  return {
    payout: fromFixed(payout),
    new_odds_after_sale: {
      yes: (newProbYes / SCALE) * 100,
      no: ((SCALE - newProbYes) / SCALE) * 100,
    },
  };
};
